import { http, HttpResponse, type HttpHandler } from "msw";

import { API_PREFIX, type PaginationMeta } from "@nasam/kernel";
import type {
  AccountAdminDto,
  ArchivedDepartmentDto,
  BranchDto,
  CompanyDto,
  DepartmentDto,
} from "../data";
import {
  accountAdminCreateWriteDtoSchema,
  accountAdminUpdateWriteDtoSchema,
  branchCreateWriteDtoSchema,
  branchUpdateWriteDtoSchema,
  departmentCreateWriteDtoSchema,
  departmentUpdateWriteDtoSchema,
  resendActivationLinkWriteDtoSchema,
} from "../data";
import {
  archivedBranchFixtures,
  archivedDepartmentDtoFixtures,
  branchFixtures,
  countryFixtures,
  currencyFixtures,
  departmentDtoFixtures,
  departmentWithUsersId,
  governorateFixtures,
  organizationAccountAdminDtos,
  organizationArchivedAdminDtos,
  organizationArchivedCompanyDtos,
  organizationCompanyDtos,
} from "./organizationFixtures";

const ARCHIVED_COMPANY_MATCH_MESSAGE =
  "توجد بالفعل شركة تحمل نفس البيانات ضمن الشركات المؤرشفة. هل ترغب في استعادة هذه الشركة؟";
const ARCHIVED_ADMIN_MATCH_MESSAGE =
  "يوجد بالفعل مسؤول بنفس البيانات ضمن المسؤولين المؤرشفين. هل ترغب في استعادته؟";
const ARCHIVED_DEPARTMENT_MATCH_MESSAGE =
  "A department with the same information already exists in the archived departments. Would you like to restore this department?";
const ARCHIVED_BRANCH_MATCH_MESSAGE =
  "A branch with the same information already exists in the archived branches. Would you like to restore this branch?";

const ACCOUNTS_PATH = `${API_PREFIX}/accounts`;
const ADMINS_PATH = `${API_PREFIX}/users/accounts-admins`;
const BRANCHES_PATH = `${API_PREFIX}/branches`;
const DEPARTMENTS_PATH = `${API_PREFIX}/departments`;

export interface OrganizationHandlerOptions {
  readonly apiBaseUrl: string;
}

type QueryValue = string | null;

function matches(value: string, query: QueryValue): boolean {
  return (
    query === null ||
    value.toLocaleLowerCase().includes(query.toLocaleLowerCase())
  );
}

function dateMatches(value: string, from: QueryValue, to: QueryValue): boolean {
  const timestamp = Date.parse(value);
  return (
    (from === null || timestamp >= Date.parse(from)) &&
    (to === null || timestamp <= Date.parse(to))
  );
}

function compare(left: string, right: string, sort: QueryValue): number {
  const result = left.localeCompare(right);
  return sort === "desc" ? -result : result;
}

function paginationMeta(
  url: URL,
  page: number,
  limit: number,
  totalCount: number,
): PaginationMeta {
  const totalPages = Math.ceil(totalCount / limit);
  const pageUrl = (targetPage: number): string => {
    const target = new URL(url);
    target.searchParams.set("page", String(targetPage));
    target.searchParams.set("limit", String(limit));
    return target.toString();
  };

  return {
    totalCount,
    currentPage: page,
    totalPages,
    firstPageUrl: pageUrl(1),
    lastPageUrl: pageUrl(Math.max(totalPages, 1)),
    nextPageUrl: page < totalPages ? pageUrl(page + 1) : null,
    previousPageUrl: page > 1 ? pageUrl(page - 1) : null,
  };
}

function positiveNumber(value: QueryValue, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function formDataString(formData: FormData, key: string): string | undefined {
  const value = formData.get(key);
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function formDataFiles(formData: FormData): readonly File[] {
  return formData
    .getAll("attachments")
    .filter((value): value is File => value instanceof File);
}

function formDataAttachmentIds(formData: FormData): readonly string[] {
  return formData
    .getAll("attachmentsIdsToBeDeleted[]")
    .filter((value): value is string => typeof value === "string");
}

function attachmentFromFile(companyId: string, file: File, index: number) {
  const now = "2026-02-01T10:00:00.000Z";
  return {
    id: `${companyId}-attachment-${String(index + 1)}`,
    fileUrl: `https://files.example.test/${file.name}`,
    extension: file.name.split(".").at(-1) ?? "file",
    createdAt: now,
    updatedAt: now,
    createdById: "user-seed",
    updatedById: null,
  };
}

function paginate<T>(
  items: readonly T[],
  url: URL,
): { data: readonly T[]; meta: PaginationMeta } {
  const page = positiveNumber(url.searchParams.get("page"), 1);
  const limit = positiveNumber(url.searchParams.get("limit"), 10);
  const meta = paginationMeta(url, page, limit, items.length);
  const offset = (page - 1) * limit;
  return { data: items.slice(offset, offset + limit), meta };
}

function filterCompanies(
  companies: readonly CompanyDto[],
  url: URL,
): readonly CompanyDto[] {
  const params = url.searchParams;
  const search = params.get("search");
  const sortBy = params.get("sortBy") ?? "createdAt";
  const sort = params.get("sort");

  return companies
    .filter(
      (company) =>
        search === null ||
        matches(
          `${company.nameEn} ${company.nameAr} ${company.email} ${company.admin?.nameEn ?? ""} ${company.admin?.nameAr ?? ""}`,
          search,
        ),
    )
    .filter((company) =>
      params.get("type") === null ? true : company.type === params.get("type"),
    )
    .filter((company) => {
      const admin = company.admin ?? null;
      return admin === null
        ? params.get("adminId") === null
        : matches(admin.id, params.get("adminId"));
    })
    .filter((company) =>
      dateMatches(
        company.createdAt,
        params.get("fromCreatedAt"),
        params.get("toCreatedAt"),
      ),
    )
    .toSorted((left, right) => {
      const value = (company: CompanyDto): string => {
        switch (sortBy) {
          case "nameEn":
            return company.nameEn;
          case "nameAr":
            return company.nameAr;
          case "email":
            return company.email;
          case "admin":
            return company.admin?.nameEn ?? "";
          default:
            return company.createdAt;
        }
      };
      return compare(value(left), value(right), sort);
    });
}

function filterAdmins(
  admins: readonly AccountAdminDto[],
  url: URL,
): readonly AccountAdminDto[] {
  const params = url.searchParams;
  const search = params.get("search");
  const sortBy = params.get("sortBy") ?? "createdAt";
  const sort = params.get("sort");

  return admins
    .filter(
      (admin) =>
        search === null ||
        matches(
          `${admin.username} ${admin.nameEn} ${admin.nameAr} ${admin.email} ${admin.account?.nameEn ?? ""} ${admin.account?.nameAr ?? ""}`,
          search,
        ),
    )
    .filter((admin) =>
      params.get("accountId") === null
        ? true
        : admin.accountId === params.get("accountId"),
    )
    .filter((admin) =>
      params.get("status") === null
        ? true
        : admin.status === params.get("status"),
    )
    .toSorted((left, right) => {
      const value = (admin: AccountAdminDto): string => {
        switch (sortBy) {
          case "id":
            return admin.id;
          case "username":
            return admin.username;
          case "nameEn":
            return admin.nameEn;
          case "nameAr":
            return admin.nameAr;
          case "accountName":
            return admin.account?.nameEn ?? "";
          case "email":
            return admin.email;
          case "status":
            return admin.status;
          default:
            return admin.createdAt;
        }
      };
      return compare(value(left), value(right), sort);
    });
}

function filterBranches(
  branches: readonly BranchDto[],
  url: URL,
): readonly BranchDto[] {
  const params = url.searchParams;
  const search = params.get("search");
  const sortBy = params.get("sortBy") ?? "createdAt";
  const sort = params.get("sort") ?? "desc";

  return branches
    .filter(
      (branch) =>
        search === null || matches(`${branch.nameEn} ${branch.nameAr}`, search),
    )
    .filter((branch) =>
      params.get("accountId") === null
        ? true
        : branch.accountId === params.get("accountId"),
    )
    .filter((branch) => {
      const isActive = params.get("isActive");
      if (isActive === null) return true;
      return branch.isActive === (isActive === "true");
    })
    .toSorted((left, right) => {
      const value = (branch: BranchDto): string => {
        switch (sortBy) {
          case "nameEn":
            return branch.nameEn;
          case "nameAr":
            return branch.nameAr;
          default:
            return branch.createdAt;
        }
      };
      return compare(value(left), value(right), sort);
    });
}

/** Either name matches, English case-insensitively (the backend's rule). */
function sameDepartmentName(names: { nameEn: string; nameAr: string }) {
  return (department: { nameEn: string; nameAr: string }): boolean =>
    department.nameEn.toLocaleLowerCase() === names.nameEn.toLocaleLowerCase() ||
    department.nameAr === names.nameAr;
}

function departmentNotFound(path: string) {
  return HttpResponse.json(
    { statusCode: 404, path, message: "Department not found" },
    { status: 404 },
  );
}

function departmentExists(path: string) {
  return HttpResponse.json(
    { statusCode: 409, path, message: "Department already exists." },
    { status: 409 },
  );
}

function filterDepartments(
  departments: readonly DepartmentDto[],
  url: URL,
): readonly DepartmentDto[] {
  const params = url.searchParams;
  const search = params.get("search");
  const sortBy = params.get("sortBy") ?? "createdAt";
  const sort = params.get("sort") ?? "desc";

  return departments
    .filter(
      (department) =>
        search === null ||
        matches(`${department.nameEn} ${department.nameAr}`, search),
    )
    .toSorted((left, right) => {
      const value = (department: DepartmentDto): string => {
        switch (sortBy) {
          case "nameEn":
            return department.nameEn;
          case "nameAr":
            return department.nameAr;
          default:
            return department.createdAt;
        }
      };
      return compare(value(left), value(right), sort);
    });
}

export function createOrganizationHandlers({
  apiBaseUrl,
}: OrganizationHandlerOptions): readonly HttpHandler[] {
  let companies = organizationCompanyDtos.map((company) => ({ ...company }));
  let admins = organizationAccountAdminDtos.map((admin) => ({ ...admin }));
  let archivedCompanies = organizationArchivedCompanyDtos.map((company) => ({
    ...company,
  }));
  let archivedAdmins = organizationArchivedAdminDtos.map((admin) => ({
    ...admin,
  }));
  let branches = branchFixtures.map((branch) => ({ ...branch }));
  let archivedBranches = archivedBranchFixtures.map((branch) => ({ ...branch }));
  const departments: DepartmentDto[] = departmentDtoFixtures.map(
    (department) => ({ ...department }),
  );
  const archivedDepartments: ArchivedDepartmentDto[] =
    archivedDepartmentDtoFixtures.map((department) => ({ ...department }));

  return [
    http.get(`${apiBaseUrl}${ACCOUNTS_PATH}`, ({ request }) => {
      const url = new URL(request.url);
      return HttpResponse.json(paginate(filterCompanies(companies, url), url));
    }),
    http.get(`${apiBaseUrl}${ACCOUNTS_PATH}/summary`, () =>
      HttpResponse.json({
        data: {
          totalAccounts: companies.length,
          totalActiveAccounts: companies.filter((company) => company.isActive)
            .length,
          totalInactiveAccounts: companies.filter(
            (company) => !company.isActive,
          ).length,
          totalAccountsAdmins: admins.length,
        },
      }),
    ),
    // Registered before `/:id` — otherwise "archived" matches the id param.
    http.get(`${apiBaseUrl}${ACCOUNTS_PATH}/archived`, ({ request }) => {
      const url = new URL(request.url);
      const params = url.searchParams;
      const search = params.get("search");
      const sort = params.get("sort") ?? "desc";
      const rows = archivedCompanies
        .filter(
          (company) =>
            (search === null ||
              matches(
                `${company.nameEn} ${company.nameAr} ${company.email} ${company.admin?.nameEn ?? ""} ${company.admin?.nameAr ?? ""} ${company.admin?.username ?? ""} ${company.admin?.email ?? ""}`,
                search,
              )) &&
            dateMatches(
              company.archivedAt,
              params.get("fromArchivedAt"),
              params.get("toArchivedAt"),
            ),
        )
        .toSorted((left, right) =>
          compare(left.archivedAt, right.archivedAt, sort),
        )
        .map((company) => ({
          id: company.id,
          nameEn: company.nameEn,
          nameAr: company.nameAr,
          email: company.email,
          admin: company.admin
            ? {
                id: company.admin.id,
                nameEn: company.admin.nameEn,
                nameAr: company.admin.nameAr,
                email: company.admin.email,
                username: company.admin.username,
              }
            : null,
          archivedAt: company.archivedAt,
        }));
      return HttpResponse.json(paginate(rows, url));
    }),
    http.get(`${apiBaseUrl}${ACCOUNTS_PATH}/:id`, ({ params }) => {
      const company = companies.find((item) => item.id === params.id);
      return company === undefined
        ? HttpResponse.json(
            {
              statusCode: 404,
              path: ACCOUNTS_PATH,
              message: "Company not found",
            },
            { status: 404 },
          )
        : HttpResponse.json({ data: company });
    }),
    http.post(`${apiBaseUrl}${ACCOUNTS_PATH}`, async ({ request }) => {
      const formData = await request.formData();

      // A create attempt whose email matches an archived account creates
      // nothing — 409 with the trimmed record and a restore prompt instead
      // (used to be 201 with this same trimmed body -- see
      // createOrganizationHttpRepositories' own company create()).
      const archivedMatch = archivedCompanies.find(
        (company) => company.email === formDataString(formData, "email"),
      );
      if (archivedMatch !== undefined) {
        return HttpResponse.json(
          {
            statusCode: 409,
            path: ACCOUNTS_PATH,
            message: ARCHIVED_COMPANY_MATCH_MESSAGE,
            code: "ARCHIVED_ACCOUNT_FOUND",
            data: {
              id: archivedMatch.id,
              nameEn: archivedMatch.nameEn,
              nameAr: archivedMatch.nameAr,
              email: archivedMatch.email,
              phoneNumber1: archivedMatch.phoneNumber1,
            },
          },
          { status: 409 },
        );
      }

      const id = `company-${String(companies.length + 1).padStart(2, "0")}`;
      const now = "2026-02-01T10:00:00.000Z";
      const currencyId = formDataString(formData, "currencyId");
      const currency =
        currencyFixtures.find((item) => item.id === currencyId) ??
        currencyFixtures[0];
      if (currency === undefined)
        throw new Error("no currency fixtures seeded");

      const uploaded = formDataFiles(formData);
      const attachments = uploaded.map((file, index) =>
        attachmentFromFile(id, file, index),
      );
      const company: CompanyDto = {
        id,
        type:
          formDataString(formData, "type") === "INDIVIDUAL"
            ? "INDIVIDUAL"
            : "COMPANY",
        nameEn: formDataString(formData, "nameEn") ?? "",
        nameAr: formDataString(formData, "nameAr") ?? "",
        address: formDataString(formData, "address") ?? null,
        remarks: formDataString(formData, "remarks") ?? null,
        email: formDataString(formData, "email") ?? "",
        phoneNumber1: formDataString(formData, "phoneNumber1") ?? "",
        phoneNumber2: formDataString(formData, "phoneNumber2") ?? null,
        fax: formDataString(formData, "fax") ?? null,
        createdAt: now,
        updatedAt: now,
        currencyId: currency.id,
        currency,
        attachments,
      };
      companies = [...companies, company];
      return HttpResponse.json(
        {
          data: {
            ...company,
            attachments: uploaded.length === 0 ? undefined : attachments,
          },
        },
        { status: 201 },
      );
    }),
    // Registered before the generic `/:id` PATCH — `/:id` is one segment and
    // never matches `/:id/restore`, but keep the specific route first anyway.
    http.patch(`${apiBaseUrl}${ACCOUNTS_PATH}/:id/restore`, ({ params }) => {
      const index = archivedCompanies.findIndex(
        (company) => company.id === params.id,
      );
      if (index === -1) {
        return HttpResponse.json(
          {
            statusCode: 404,
            path: `${ACCOUNTS_PATH}/${String(params.id)}/restore`,
            message: "Account not found",
          },
          { status: 404 },
        );
      }
      const [restored] = archivedCompanies.splice(index, 1);
      if (restored === undefined) throw new Error("unreachable");
      const active = {
        ...restored,
        isActive: true,
        updatedAt: "2026-02-01T10:00:00.000Z",
      };
      companies = [...companies, active];
      return HttpResponse.json({
        data: active,
        message: "Company restored successfully.",
      });
    }),
    http.patch(
      `${apiBaseUrl}${ACCOUNTS_PATH}/:id`,
      async ({ params, request }) => {
        const formData = await request.formData();
        const index = companies.findIndex(
          (company) => company.id === params.id,
        );
        if (index === -1) {
          return HttpResponse.json(
            {
              statusCode: 404,
              path: ACCOUNTS_PATH,
              message: "Company not found",
            },
            { status: 404 },
          );
        }
        const current = companies[index] as CompanyDto;
        const attachmentsToKeep = (current.attachments ?? []).filter(
          (attachment) =>
            !formDataAttachmentIds(formData).includes(attachment.id),
        );
        const uploaded = formDataFiles(formData);
        const nextAttachments = [
          ...attachmentsToKeep,
          ...uploaded.map((file, attachmentIndex) =>
            attachmentFromFile(
              current.id,
              file,
              (current.attachments ?? []).length + attachmentIndex,
            ),
          ),
        ];
        // Persist the merge regardless, so a later GET reflects it — only the
        // *response body* omits `attachments` when nothing was uploaded.
        const updated: CompanyDto = {
          ...current,
          type:
            formDataString(formData, "type") === "INDIVIDUAL"
              ? "INDIVIDUAL"
              : formDataString(formData, "type") === "COMPANY"
                ? "COMPANY"
                : current.type,
          nameEn: formDataString(formData, "nameEn") ?? current.nameEn,
          nameAr: formDataString(formData, "nameAr") ?? current.nameAr,
          address: formDataString(formData, "address") ?? current.address,
          remarks: formDataString(formData, "remarks") ?? current.remarks,
          email: formDataString(formData, "email") ?? current.email,
          phoneNumber1:
            formDataString(formData, "phoneNumber1") ?? current.phoneNumber1,
          phoneNumber2:
            formDataString(formData, "phoneNumber2") ?? current.phoneNumber2,
          fax: formDataString(formData, "fax") ?? current.fax,
          attachments: nextAttachments,
          updatedAt: "2026-02-01T10:00:00.000Z",
        };
        companies = companies.with(index, updated);
        return HttpResponse.json({
          data: {
            ...updated,
            attachments: uploaded.length === 0 ? undefined : nextAttachments,
            // `admin` is never included on the update response, per the contract.
            admin: undefined,
          },
        });
      },
    ),
    http.delete(`${apiBaseUrl}${ACCOUNTS_PATH}/:id`, ({ params }) => {
      const usersCount = admins.filter(
        (admin) => admin.accountId === params.id,
      ).length;
      const deleted = companies.find((company) => company.id === params.id);
      admins = admins.filter((admin) => admin.accountId !== params.id);
      companies = companies.filter((company) => company.id !== params.id);
      // A soft-delete: the row moves to the archive, where a later create
      // attempt with the same email will rediscover it.
      if (deleted !== undefined) {
        archivedCompanies = [
          ...archivedCompanies,
          {
            ...deleted,
            isActive: false,
            archivedAt: "2026-02-01T10:00:00.000Z",
          },
        ];
      }
      return HttpResponse.json({
        data: { accountsCount: 1, usersCount },
      });
    }),
    http.get(`${apiBaseUrl}${ADMINS_PATH}`, ({ request }) => {
      const url = new URL(request.url);
      return HttpResponse.json(paginate(filterAdmins(admins, url), url));
    }),
    // Registered before `/:id` — otherwise "archived" matches the id param.
    http.get(`${apiBaseUrl}${ADMINS_PATH}/archived`, ({ request }) => {
      const url = new URL(request.url);
      const params = url.searchParams;
      const search = params.get("search");
      const sort = params.get("sort") ?? "desc";
      const rows = archivedAdmins
        .filter(
          (admin) =>
            (search === null ||
              matches(
                `${admin.username} ${admin.nameEn} ${admin.nameAr} ${admin.email} ${admin.account?.nameEn ?? ""} ${admin.account?.nameAr ?? ""}`,
                search,
              )) &&
            dateMatches(
              admin.archivedAt,
              params.get("fromArchivedAt"),
              params.get("toArchivedAt"),
            ),
        )
        .toSorted((left, right) =>
          compare(left.archivedAt, right.archivedAt, sort),
        )
        .map((admin) => ({
          id: admin.id,
          username: admin.username,
          email: admin.email,
          nameEn: admin.nameEn,
          nameAr: admin.nameAr,
          account: admin.account ?? null,
          archivedAt: admin.archivedAt,
        }));
      return HttpResponse.json(paginate(rows, url));
    }),
    http.get(`${apiBaseUrl}${ADMINS_PATH}/:id`, ({ params }) => {
      const admin = admins.find((item) => item.id === params.id);
      return admin === undefined
        ? HttpResponse.json(
            { statusCode: 404, path: ADMINS_PATH, message: "Admin not found" },
            { status: 404 },
          )
        : HttpResponse.json({ data: admin });
    }),
    http.post(
      `${apiBaseUrl}${API_PREFIX}/users/account-admin`,
      async ({ request }) => {
        const input = accountAdminCreateWriteDtoSchema.parse(
          await request.json(),
        );

        // A create attempt matching an archived admin creates nothing — 409
        // with the trimmed record and a restore prompt (used to be 201 with
        // this same trimmed body -- see createOrganizationHttpRepositories'
        // own admin create()).
        const archivedMatch = archivedAdmins.find(
          (admin) =>
            admin.email === input.email || admin.username === input.username,
        );
        if (archivedMatch !== undefined) {
          return HttpResponse.json(
            {
              statusCode: 409,
              path: `${API_PREFIX}/users/account-admin`,
              message: ARCHIVED_ADMIN_MATCH_MESSAGE,
              code: "ARCHIVED_ADMIN_FOUND",
              data: {
                id: archivedMatch.id,
                username: archivedMatch.username,
                email: archivedMatch.email,
                nameEn: archivedMatch.nameEn,
                nameAr: archivedMatch.nameAr,
              },
            },
            { status: 409 },
          );
        }

        const company = companies.find((item) => item.id === input.accountId);
        if (company === undefined) {
          return HttpResponse.json(
            {
              statusCode: 404,
              path: ADMINS_PATH,
              message: "Company not found",
            },
            { status: 404 },
          );
        }
        const now = "2026-02-01T10:00:00.000Z";
        const admin: AccountAdminDto = {
          id: `admin-${String(admins.length + 1).padStart(2, "0")}`,
          username: input.username,
          email: input.email,
          nameEn: input.nameEn,
          nameAr: input.nameAr,
          phoneNumber: input.phoneNumber,
          enable2FA: true,
          status: "PENDING_ACTIVATION",
          createdAt: now,
          updatedAt: now,
          accountId: company.id,
          typeId: 3,
          type: { id: 3, slug: "CLIENT_ADMIN" },
          account: {
            id: company.id,
            nameEn: company.nameEn,
            nameAr: company.nameAr,
          },
        };
        admins = [...admins, admin];
        return HttpResponse.json({ data: admin }, { status: 201 });
      },
    ),
    http.patch(`${apiBaseUrl}${ADMINS_PATH}/:id/restore`, ({ params }) => {
      const index = archivedAdmins.findIndex((admin) => admin.id === params.id);
      if (index === -1) {
        return HttpResponse.json(
          {
            statusCode: 404,
            path: `${ADMINS_PATH}/${String(params.id)}/restore`,
            message: "User not found",
          },
          { status: 404 },
        );
      }
      const archived = archivedAdmins[index];
      if (archived === undefined) throw new Error("unreachable");
      if (admins.some((admin) => admin.accountId === archived.accountId)) {
        return HttpResponse.json(
          {
            statusCode: 409,
            path: `${ADMINS_PATH}/${String(params.id)}/restore`,
            message: "This account already has an admin",
          },
          { status: 409 },
        );
      }
      archivedAdmins.splice(index, 1);
      // deletedAt cleared, type promoted back, ACTIVE unless never activated.
      const status =
        archived.status === "PENDING_ACTIVATION"
          ? "PENDING_ACTIVATION"
          : "ACTIVE";
      const restored: AccountAdminDto = {
        ...archived,
        status,
        typeId: 3,
        type: { id: 3, slug: "CLIENT_ADMIN" },
        updatedAt: "2026-02-01T10:00:00.000Z",
      };
      admins = [...admins, restored];
      // The live endpoint returns the full admin, not a trimmed subset.
      return HttpResponse.json({
        data: restored,
        message: "تمت استعادة المسؤول بنجاح.",
      });
    }),
    http.patch(
      `${apiBaseUrl}${ADMINS_PATH}/:id`,
      async ({ params, request }) => {
        const input = accountAdminUpdateWriteDtoSchema.parse(
          await request.json(),
        );
        const index = admins.findIndex((admin) => admin.id === params.id);
        if (index === -1) {
          return HttpResponse.json(
            { statusCode: 404, path: ADMINS_PATH, message: "Admin not found" },
            { status: 404 },
          );
        }
        const current = admins[index] as AccountAdminDto;
        if (
          input.status === "ACTIVE" &&
          current.status === "PENDING_ACTIVATION"
        ) {
          return HttpResponse.json(
            {
              statusCode: 409,
              path: ADMINS_PATH,
              message:
                "Cannot activate an administrator before they activate their own account.",
            },
            { status: 409 },
          );
        }
        const updated: AccountAdminDto = {
          ...current,
          ...(input.nameEn === undefined ? {} : { nameEn: input.nameEn }),
          ...(input.nameAr === undefined ? {} : { nameAr: input.nameAr }),
          ...(input.email === undefined ? {} : { email: input.email }),
          ...(input.phoneNumber === undefined
            ? {}
            : { phoneNumber: input.phoneNumber }),
          ...(input.status === undefined ? {} : { status: input.status }),
          updatedAt: "2026-02-01T10:00:00.000Z",
        };
        admins = admins.with(index, updated);
        return HttpResponse.json({ data: updated });
      },
    ),
    http.delete(`${apiBaseUrl}${ADMINS_PATH}/:id`, ({ params }) => {
      const deleted = admins.find((admin) => admin.id === params.id);
      admins = admins.filter((admin) => admin.id !== params.id);
      // Soft-delete: archived and demoted to the staff counterpart, where a
      // later create with the same email/username rediscovers it.
      if (deleted !== undefined) {
        archivedAdmins = [
          ...archivedAdmins,
          {
            ...deleted,
            typeId: 4,
            type: { id: 4, slug: "CLIENT_EMPLOYEE" },
            archivedAt: "2026-02-01T10:00:00.000Z",
          },
        ];
      }
      return HttpResponse.json({ message: "Deleted successfully" });
    }),
    http.post(
      `${apiBaseUrl}${API_PREFIX}/auth/resend-activation-link`,
      async ({ request }) => {
        const { email } = resendActivationLinkWriteDtoSchema.parse(
          await request.json(),
        );
        return admins.some((admin) => admin.email === email)
          ? HttpResponse.json({
              message:
                "A new activation link has been sent to your registered email address.",
            })
          : HttpResponse.json(
              { statusCode: 404, path: "", message: "No user with this email" },
              { status: 404 },
            );
      },
    ),
    http.get(`${apiBaseUrl}${API_PREFIX}/currencies`, ({ request }) => {
      const url = new URL(request.url);
      return HttpResponse.json(paginate(currencyFixtures, url));
    }),
    http.get(`${apiBaseUrl}${API_PREFIX}/countries`, ({ request }) => {
      const url = new URL(request.url);
      return HttpResponse.json(paginate(countryFixtures, url));
    }),
    http.get(
      `${apiBaseUrl}${API_PREFIX}/countries/:countryId/governorates`,
      ({ request, params }) => {
        const country = countryFixtures.find(
          (candidate) => candidate.id === params.countryId,
        );
        if (country === undefined) {
          return HttpResponse.json(
            {
              statusCode: 404,
              path: "",
              message: "Country not found",
            },
            { status: 404 },
          );
        }
        const url = new URL(request.url);
        const inCountry = governorateFixtures.filter(
          (governorate) => governorate.countryId === params.countryId,
        );
        return HttpResponse.json(paginate(inCountry, url));
      },
    ),
    http.get(`${apiBaseUrl}${BRANCHES_PATH}`, ({ request }) => {
      const url = new URL(request.url);
      return HttpResponse.json(paginate(filterBranches(branches, url), url));
    }),
    // Registered before `/:id` — otherwise "archived" matches the id param.
    http.get(`${apiBaseUrl}${BRANCHES_PATH}/archived`, ({ request }) => {
      const url = new URL(request.url);
      const params = url.searchParams;
      const search = params.get("search");
      const sort = params.get("sort") ?? "desc";
      const rows = archivedBranches
        .filter(
          (branch) =>
            search === null || matches(`${branch.nameEn} ${branch.nameAr}`, search),
        )
        .filter((branch) =>
          params.get("accountId") === null
            ? true
            : branch.account.id === params.get("accountId"),
        )
        .filter((branch) =>
          dateMatches(
            branch.archivedAt,
            params.get("fromArchivedAt"),
            params.get("toArchivedAt"),
          ),
        )
        .toSorted((left, right) => compare(left.archivedAt, right.archivedAt, sort));
      return HttpResponse.json(paginate(rows, url));
    }),
    http.get(`${apiBaseUrl}${BRANCHES_PATH}/:id`, ({ params }) => {
      const branch = branches.find((item) => item.id === params.id);
      return branch === undefined
        ? HttpResponse.json(
            { statusCode: 404, path: BRANCHES_PATH, message: "Branch not found" },
            { status: 404 },
          )
        : HttpResponse.json({ data: branch });
    }),
    http.post(`${apiBaseUrl}${BRANCHES_PATH}`, async ({ request }) => {
      const body = branchCreateWriteDtoSchema.parse(await request.json());
      const accountId = body.accountId ?? "company-01";

      const archivedMatch = archivedBranches.find(
        (branch) =>
          branch.account.id === accountId &&
          (branch.nameEn === body.nameEn || branch.nameAr === body.nameAr),
      );
      if (archivedMatch !== undefined) {
        return HttpResponse.json(
          {
            statusCode: 409,
            path: BRANCHES_PATH,
            message: ARCHIVED_BRANCH_MATCH_MESSAGE,
            code: "ARCHIVED_BRANCH_FOUND",
            data: {
              id: archivedMatch.id,
              nameEn: archivedMatch.nameEn,
              nameAr: archivedMatch.nameAr,
              accountId,
              deletedAt: archivedMatch.archivedAt,
            },
          },
          { status: 409 },
        );
      }
      // Fixture-driven 409, same trick authHandlers.ts uses for its OTP
      // success code -- a magic input value instead of real duplicate-name
      // bookkeeping against a living branch across requests.
      if (body.nameEn === "Duplicate Branch") {
        return HttpResponse.json(
          { statusCode: 409, path: BRANCHES_PATH, message: "English name already exists" },
          { status: 409 },
        );
      }

      const now = new Date().toISOString();
      const country = countryFixtures.find((item) => item.id === body.countryId);
      const governorate = governorateFixtures.find(
        (item) => item.id === body.governorateId,
      );
      const created: BranchDto = {
        id: "created-branch-id",
        nameEn: body.nameEn,
        nameAr: body.nameAr,
        isMain: false,
        isActive: true,
        countryId: body.countryId,
        governorateId: body.governorateId,
        country:
          country === undefined
            ? null
            : { id: country.id, nameEn: country.nameEn, nameAr: country.nameAr },
        governorate:
          governorate === undefined
            ? null
            : {
                id: governorate.id,
                nameEn: governorate.nameEn,
                nameAr: governorate.nameAr,
              },
        latitude: body.latitude,
        longitude: body.longitude,
        createdAt: now,
        updatedAt: now,
        accountId,
        createdById: "user-1",
        updatedById: null,
      };
      branches = [...branches, created];
      return HttpResponse.json(
        { message: "Created successfully", data: created },
        { status: 201 },
      );
    }),
    // Registered before the generic `/:id` PATCH — `/:id` is one segment and
    // never matches `/:id/restore`, but keep the specific route first anyway.
    http.patch(`${apiBaseUrl}${BRANCHES_PATH}/:id/restore`, ({ params }) => {
      const restorePath = `${BRANCHES_PATH}/${String(params.id)}/restore`;
      const index = archivedBranches.findIndex((branch) => branch.id === params.id);
      if (index === -1) {
        return HttpResponse.json(
          { statusCode: 404, path: restorePath, message: "Branch not found" },
          { status: 404 },
        );
      }
      const [restored] = archivedBranches.splice(index, 1);
      if (restored === undefined) throw new Error("unreachable");

      const collision = branches.find(
        (branch) =>
          branch.accountId === restored.account.id &&
          (branch.nameEn === restored.nameEn || branch.nameAr === restored.nameAr),
      );
      if (collision !== undefined) {
        archivedBranches = [...archivedBranches, restored];
        return HttpResponse.json(
          {
            statusCode: 409,
            path: restorePath,
            message: "English name already exists",
          },
          { status: 409 },
        );
      }

      const now = new Date().toISOString();
      const active: BranchDto = {
        id: restored.id,
        nameEn: restored.nameEn,
        nameAr: restored.nameAr,
        isMain: false,
        isActive: restored.isActive,
        countryId: null,
        governorateId: null,
        country: null,
        governorate: null,
        latitude: null,
        longitude: null,
        createdAt: now,
        updatedAt: now,
        accountId: restored.account.id,
        createdById: "user-1",
        updatedById: null,
      };
      branches = [...branches, active];
      return HttpResponse.json({
        data: active,
        message: "Branch restored successfully.",
      });
    }),
    http.patch(
      `${apiBaseUrl}${BRANCHES_PATH}/:id`,
      async ({ params, request }) => {
        const body = branchUpdateWriteDtoSchema.parse(await request.json());
        const index = branches.findIndex((branch) => branch.id === params.id);
        if (index === -1) {
          return HttpResponse.json(
            { statusCode: 404, path: BRANCHES_PATH, message: "Branch not found" },
            { status: 404 },
          );
        }
        const current = branches[index];
        if (current === undefined) throw new Error("unreachable");

        const collision = branches.find(
          (branch) =>
            branch.id !== current.id &&
            branch.accountId === current.accountId &&
            ((body.nameEn !== undefined && branch.nameEn === body.nameEn) ||
              (body.nameAr !== undefined && branch.nameAr === body.nameAr)),
        );
        if (collision !== undefined) {
          return HttpResponse.json(
            {
              statusCode: 409,
              path: `${BRANCHES_PATH}/${String(params.id)}`,
              message: "English name already exists",
            },
            { status: 409 },
          );
        }

        const country =
          body.countryId === undefined
            ? undefined
            : countryFixtures.find((item) => item.id === body.countryId);
        const governorate =
          body.governorateId === undefined
            ? undefined
            : governorateFixtures.find((item) => item.id === body.governorateId);
        const updated: BranchDto = {
          ...current,
          ...(body.nameEn === undefined ? {} : { nameEn: body.nameEn }),
          ...(body.nameAr === undefined ? {} : { nameAr: body.nameAr }),
          ...(body.countryId === undefined
            ? {}
            : {
                countryId: body.countryId,
                country:
                  country === undefined
                    ? null
                    : { id: country.id, nameEn: country.nameEn, nameAr: country.nameAr },
              }),
          ...(body.governorateId === undefined
            ? {}
            : {
                governorateId: body.governorateId,
                governorate:
                  governorate === undefined
                    ? null
                    : {
                        id: governorate.id,
                        nameEn: governorate.nameEn,
                        nameAr: governorate.nameAr,
                      },
              }),
          ...(body.latitude === undefined ? {} : { latitude: body.latitude }),
          ...(body.longitude === undefined ? {} : { longitude: body.longitude }),
          ...(body.isActive === undefined ? {} : { isActive: body.isActive }),
          updatedAt: new Date().toISOString(),
        };
        branches = branches.map((branch, itemIndex) =>
          itemIndex === index ? updated : branch,
        );
        return HttpResponse.json({ message: "Updated successfully", data: updated });
      },
    ),
    http.delete(`${apiBaseUrl}${BRANCHES_PATH}/:id`, ({ params }) => {
      const deletePath = `${BRANCHES_PATH}/${String(params.id)}`;
      const index = branches.findIndex((branch) => branch.id === params.id);
      if (index === -1) {
        return HttpResponse.json(
          { statusCode: 404, path: deletePath, message: "Branch not found" },
          { status: 404 },
        );
      }
      const target = branches[index];
      if (target === undefined) throw new Error("unreachable");
      if (target.isMain) {
        return HttpResponse.json(
          {
            statusCode: 400,
            path: deletePath,
            message: "The Main Branch cannot be deleted.",
          },
          { status: 400 },
        );
      }
      branches.splice(index, 1);
      const account = companies.find((company) => company.id === target.accountId);
      archivedBranches = [
        ...archivedBranches,
        {
          id: target.id,
          nameEn: target.nameEn,
          nameAr: target.nameAr,
          isActive: target.isActive,
          account: {
            id: target.accountId,
            nameEn: account?.nameEn ?? "",
            nameAr: account?.nameAr ?? "",
          },
          archivedAt: new Date().toISOString(),
        },
      ];
      return HttpResponse.json({ message: "Branch deleted successfully." });
    }),
    http.get(`${apiBaseUrl}${DEPARTMENTS_PATH}`, ({ request }) => {
      const url = new URL(request.url);
      return HttpResponse.json(
        paginate(filterDepartments(departments, url), url),
      );
    }),
    // Registered before `/:id` — otherwise "archived" matches the id param.
    http.get(`${apiBaseUrl}${DEPARTMENTS_PATH}/archived`, ({ request }) => {
      const url = new URL(request.url);
      const params = url.searchParams;
      const search = params.get("search");
      const sort = params.get("sort") ?? "desc";
      const rows = archivedDepartments
        .filter(
          (department) =>
            search === null ||
            matches(`${department.nameEn} ${department.nameAr}`, search),
        )
        .filter((department) =>
          dateMatches(
            department.archivedAt,
            params.get("fromArchivedAt"),
            params.get("toArchivedAt"),
          ),
        )
        .toSorted((left, right) =>
          compare(left.archivedAt, right.archivedAt, sort),
        );
      return HttpResponse.json(paginate(rows, url));
    }),
    http.get(`${apiBaseUrl}${DEPARTMENTS_PATH}/:id`, ({ params }) => {
      const department = departments.find((item) => item.id === params.id);
      return department === undefined
        ? departmentNotFound(`${DEPARTMENTS_PATH}/${String(params.id)}`)
        : HttpResponse.json({ data: department });
    }),
    http.post(`${apiBaseUrl}${DEPARTMENTS_PATH}`, async ({ request }) => {
      const body = departmentCreateWriteDtoSchema.parse(await request.json());
      // A living match wins outright (plain 409, no restore offered).
      if (departments.some(sameDepartmentName(body))) {
        return departmentExists(DEPARTMENTS_PATH);
      }
      const archivedMatch = archivedDepartments.find(sameDepartmentName(body));
      if (archivedMatch !== undefined) {
        return HttpResponse.json(
          {
            statusCode: 409,
            path: DEPARTMENTS_PATH,
            message: ARCHIVED_DEPARTMENT_MATCH_MESSAGE,
            code: "ARCHIVED_DEPARTMENT_FOUND",
            data: {
              id: archivedMatch.id,
              nameEn: archivedMatch.nameEn,
              nameAr: archivedMatch.nameAr,
              accountId: archivedMatch.accountId,
              deletedAt: archivedMatch.archivedAt,
            },
          },
          { status: 409 },
        );
      }
      const now = new Date().toISOString();
      const created: DepartmentDto = {
        id: `department-created-${String(departments.length + 1)}`,
        nameEn: body.nameEn,
        nameAr: body.nameAr,
        remarks: body.remarks ?? null,
        isActive: true,
        createdAt: now,
        updatedAt: now,
        accountId: "company-01",
        createdById: "user-1",
        updatedById: null,
      };
      departments.push(created);
      return HttpResponse.json(
        { message: "Department created successfully.", data: created },
        { status: 201 },
      );
    }),
    http.patch(
      `${apiBaseUrl}${DEPARTMENTS_PATH}/:id/restore`,
      ({ params }) => {
        const path = `${DEPARTMENTS_PATH}/${String(params.id)}/restore`;
        const index = archivedDepartments.findIndex(
          (item) => item.id === params.id,
        );
        const target = archivedDepartments[index];
        if (target === undefined) return departmentNotFound(path);
        if (departments.some(sameDepartmentName(target))) {
          return departmentExists(path);
        }
        archivedDepartments.splice(index, 1);
        const restored: DepartmentDto = {
          id: target.id,
          nameEn: target.nameEn,
          nameAr: target.nameAr,
          remarks: target.remarks,
          isActive: true,
          createdAt: target.createdAt,
          updatedAt: new Date().toISOString(),
          accountId: target.accountId ?? null,
          createdById: target.createdById ?? null,
          updatedById: target.updatedById ?? null,
          account: target.account ?? null,
        };
        departments.push(restored);
        return HttpResponse.json({
          message: "Department restored successfully.",
          data: restored,
        });
      },
    ),
    http.patch(
      `${apiBaseUrl}${DEPARTMENTS_PATH}/:id`,
      async ({ params, request }) => {
        const path = `${DEPARTMENTS_PATH}/${String(params.id)}`;
        const body = departmentUpdateWriteDtoSchema.parse(await request.json());
        const index = departments.findIndex((item) => item.id === params.id);
        const current = departments[index];
        if (current === undefined) return departmentNotFound(path);
        const collision = departments.some(
          (department) =>
            department.id !== current.id &&
            ((body.nameEn !== undefined &&
              department.nameEn.toLocaleLowerCase() ===
                body.nameEn.toLocaleLowerCase()) ||
              (body.nameAr !== undefined && department.nameAr === body.nameAr)),
        );
        if (collision) return departmentExists(path);
        const updated: DepartmentDto = {
          ...current,
          ...(body.nameEn === undefined ? {} : { nameEn: body.nameEn }),
          ...(body.nameAr === undefined ? {} : { nameAr: body.nameAr }),
          ...(body.isActive === undefined ? {} : { isActive: body.isActive }),
          ...(body.remarks === undefined ? {} : { remarks: body.remarks }),
          updatedAt: new Date().toISOString(),
        };
        departments[index] = updated;
        return HttpResponse.json({
          message: "Updated successfully",
          data: updated,
        });
      },
    ),
    http.delete(`${apiBaseUrl}${DEPARTMENTS_PATH}/:id`, ({ params }) => {
      const deletePath = `${DEPARTMENTS_PATH}/${String(params.id)}`;
      const index = departments.findIndex((item) => item.id === params.id);
      const target = departments[index];
      if (target === undefined) return departmentNotFound(deletePath);
      if (params.id === departmentWithUsersId) {
        return HttpResponse.json(
          {
            statusCode: 409,
            path: deletePath,
            message:
              "This Department cannot be deleted because one or more Users are assigned to it.",
          },
          { status: 409 },
        );
      }
      departments.splice(index, 1);
      archivedDepartments.push({
        ...target,
        accountId: target.accountId ?? "company-01",
        archivedAt: new Date().toISOString(),
      });
      return HttpResponse.json({
        message: "Department deleted successfully.",
      });
    }),
  ];
}
